/**
 * CollabFind – søk på skapere som er ledige for samarbeid.
 * Filtre: type · plattform · datointervall · følger min/max
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, Users, ShieldCheck, Send, CalendarCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { formatFollowers } from "./CollabAccounts";
import CollabRequestComposer from "./CollabRequestComposer";
import MultiDatePicker from "./MultiDatePicker";const COLLAB_TYPES = ["sfs", "mass", "l4l", "story_swap"];
const PLATFORMS = ["onlyfans", "fansly", "snapchat", "instagram", "x", "tiktok", "reddit", "bluesky", "other"];

export default function CollabFind() {
  const { t } = useTranslation();
  const [type, setType] = useState("");
  const [platform, setPlatform] = useState("");
  const [selectedDates, setSelectedDates] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [followerMin, setFollowerMin] = useState("");
  const [followerMax, setFollowerMax] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingAllDates, setLoadingAllDates] = useState(false);
  const [composeFor, setComposeFor] = useState(null); // {creator, slot}
  const [availabilityByDate, setAvailabilityByDate] = useState({});
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  React.useEffect(() => {
    const params = { month: currentMonth };
    if (platform) params.platform = platform;
    if (followerMin !== "") params.follower_min = Number(followerMin);
    if (followerMax !== "") params.follower_max = Number(followerMax);
    let cancelled = false;
    api.get("/collab/find/availability", { params })
      .then((r) => { if (!cancelled) setAvailabilityByDate(r.data?.availability || {}); })
      .catch(() => { if (!cancelled) setAvailabilityByDate({}); });
    return () => { cancelled = true; };
  }, [currentMonth, platform, followerMin, followerMax]);

  const pickAllMyDates = async () => {
    setLoadingAllDates(true);
    try {
      const today = new Date();
      const inNinety = new Date(Date.now() + 90 * 86400000);
      const iso = (d) => d.toISOString().slice(0, 10);
      const start = dateFrom || iso(today);
      const end = dateTo || iso(inNinety);
      const r = await api.get(`/collab/calendar/me?start=${start}&end=${end}`);
      const mine = (r.data?.slots || [])
        .filter((s) => s.is_available && s.date >= iso(today))
        .map((s) => s.date);
      if (mine.length === 0) {
        toast.error("Du har ingen ledige datoer i valgt periode. Legg til noen i kalenderen først.");
      } else {
        setSelectedDates(mine);
        toast.success(`Valgte ${mine.length} datoer fra din kalender.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke hente kalenderen.");
    } finally {
      setLoadingAllDates(false);
    }
  };

  const runSearch = async () => {
    // Bygg URL og åpne resultatene i ny fane så de ikke gjemmes under kalenderen.
    const sp = new URLSearchParams();
    if (type) sp.set("type", type);
    if (platform) sp.set("platform", platform);
    if (selectedDates.length > 0) {
      sp.set("dates", selectedDates.join(","));
    } else if (dateFrom || dateTo) {
      if (dateFrom) sp.set("date_start", dateFrom);
      if (dateTo) sp.set("date_end", dateTo);
    }
    if (followerMin !== "") sp.set("follower_min", String(Number(followerMin)));
    if (followerMax !== "") sp.set("follower_max", String(Number(followerMax)));
    const url = `/skaper/samarbeid/sok?${sp.toString()}`;
    const w = window.open(url, "_blank", "noopener,noreferrer");
    setResults([]); // gjør at hint vises i UI
    if (!w) {
      // popup-blokk → fall tilbake til samme fane
      window.location.href = url;
    }
  };

  return (
    <section data-testid="collab-find">
      <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-2">{t("collab.find_title")}</h2>
      <p className="text-sm text-[var(--cream-soft)] mb-6">{t("collab.find_subtitle")}</p>

      <div className="panel-tight p-5 mb-6 space-y-4" data-testid="find-filters">
        <div>
          <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
            {t("collab.type_label")}
          </label>
          <div className="flex flex-wrap gap-1">
            <FilterChip active={type === ""} onClick={() => setType("")}>{t("collab.all_types")}</FilterChip>
            {COLLAB_TYPES.map((tp) => (
              <FilterChip key={tp} active={type === tp} onClick={() => setType(tp)}>{tp.toUpperCase()}</FilterChip>
            ))}
          </div>
        </div>

        <div>
          <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
            {t("collab.platform_label")}
          </label>
          <div className="flex flex-wrap gap-1">
            <FilterChip active={platform === ""} onClick={() => setPlatform("")}>{t("collab.all_platforms")}</FilterChip>
            {PLATFORMS.map((p) => (
              <FilterChip key={p} active={platform === p} onClick={() => setPlatform(p)}>{p.toUpperCase()}</FilterChip>
            ))}
          </div>
        </div>

        {/* Følger-filter + Søk øverst for å komprimere */}
        <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
              {t("collab.follower_min")}
            </label>
            <input type="number" min={0} value={followerMin}
              onChange={(e) => setFollowerMin(e.target.value)}
              placeholder="0"
              className="input-editorial w-full" data-testid="find-follower-min" />
          </div>
          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
              {t("collab.follower_max")}
            </label>
            <input type="number" min={0} value={followerMax}
              onChange={(e) => setFollowerMax(e.target.value)}
              placeholder="∞"
              className="input-editorial w-full" data-testid="find-follower-max" />
          </div>
          <button onClick={runSearch} disabled={loading}
            className="btn-magenta inline-flex items-center gap-2 disabled:opacity-40 h-[42px]"
            data-testid="find-search-btn">
            <Search className="h-4 w-4" /> {loading ? t("collab.searching") : t("collab.search")}
          </button>
        </div>

        <div>
          <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
            {t("collab.pick_dates")}
          </label>

          {/* Rask-valg: alle mine ledige datoer */}
          <div className="mb-3">
            <button onClick={pickAllMyDates} disabled={loadingAllDates}
              type="button"
              className="btn-magenta text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
              data-testid="find-all-my-dates">
              <CalendarCheck className="h-3.5 w-3.5" />
              {loadingAllDates ? "Henter…" : "Søk på alle mine ledige datoer"}
            </button>
            <p className="text-[10px] font-meta text-[var(--cream-faint)] mt-1">
              Fyller inn alle datoene der du er markert ledig (i valgt fra-til-periode, eller neste 90 dager).
            </p>
          </div>

          {/* Fra-til-periode */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <label className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-faint)] block mb-1">FRA</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="input-editorial w-full" data-testid="find-date-from" />
            </div>
            <div>
              <label className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-faint)] block mb-1">TIL</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="input-editorial w-full" data-testid="find-date-to" />
            </div>
          </div>
          <p className="text-[10px] font-meta text-[var(--cream-faint)] mb-3">
            Angi periode ELLER huk av spesifikke datoer under. Spesifikke datoer overstyrer perioden.
          </p>

          <MultiDatePicker value={selectedDates} onChange={setSelectedDates}
            dotsByDate={availabilityByDate}
            onMonthChange={setCurrentMonth}
            data-testid="find-dates" />
          <p className="text-xs text-[var(--cream-faint)] mt-1">{t("collab.pick_dates_hint")}</p>
          {selectedDates.length > 0 && (
            <button onClick={() => setSelectedDates([])} type="button"
              className="mt-2 font-meta text-[10px] text-[var(--cream-soft)] hover:text-[var(--magenta)] underline"
              data-testid="find-clear-dates">
              Fjern valgte datoer ({selectedDates.length})
            </button>
          )}
        </div>
      </div>

      {results !== null && (
        <div data-testid="find-results-hint" className="panel-tight p-4 text-sm text-[var(--cream-soft)] text-center">
          {t("collab.results_opened_new_tab", "Resultatene åpnet i ny fane.")}
        </div>
      )}

      {composeFor && (
        <CollabRequestComposer
          creator={composeFor.creator}
          slot={composeFor.slot}
          accounts={composeFor.accounts}
          defaultType={type}
          defaultPlatform={platform}
          onClose={() => setComposeFor(null)}
          onSent={() => setComposeFor(null)}
        />
      )}
    </section>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button onClick={onClick}
      className={`px-3 py-1.5 font-meta text-[11px] border transition-colors ${active ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10" : "border-white/15 text-[var(--cream-soft)] hover:border-white/30"}`}>
      {children}
    </button>
  );
}

function CreatorResultCard({ item, platformFilter, onCompose }) {
  const { t } = useTranslation();
  const c = item.creator;
  const relevant = platformFilter
    ? item.accounts.filter((a) => a.platform === platformFilter)
    : item.accounts;
  const score = c.collab_reliability_score;

  return (
    <li className="panel-tight p-4" data-testid={`find-result-${c.username}`}>
      <div className="flex items-start gap-3">
        {c.avatar_url ? (
          <img src={c.avatar_url} alt="" className="w-12 h-12 object-cover border border-white/10" />
        ) : (
          <div className="w-12 h-12 bg-[var(--magenta)]/20 border border-white/10 flex items-center justify-center font-meta text-lg text-[var(--magenta)]">
            {(c.username || "?")[0].toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <Link to={`/u/${c.username}`} className="font-fraunces italic text-xl text-[var(--cream)] hover:text-[var(--magenta)]">
              {c.creator_display_name || c.username}
            </Link>
            <span className="text-xs text-[var(--cream-faint)]">@{c.username}</span>
            {score !== null && score !== undefined && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-[var(--gold)]/30 text-[var(--gold)] font-meta text-[10px]">
                <ShieldCheck className="h-3 w-3" /> {score}% · {item.creator.collab_completed_count || 0} {t("collab.completed_short")}
              </span>
            )}
          </div>
          {c.creator_bio && (
            <p className="text-sm text-[var(--cream-soft)] mt-1 line-clamp-2">{c.creator_bio}</p>
          )}
        </div>
      </div>

      {/* Plattform-kontoer */}
      {relevant.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {relevant.map((a) => (
            <span key={a.id}
              className="inline-flex items-center gap-1.5 px-2 py-1 border border-white/10 text-xs text-[var(--cream-soft)]">
              <span className="font-meta text-[9px] tracking-[0.15em] text-[var(--cream-faint)]">{a.platform.toUpperCase()}</span>
              <span>@{a.handle}</span>
              <Users className="h-3 w-3 text-[var(--cream-faint)]" />
              <span>{formatFollowers(a.follower_count)}</span>
            </span>
          ))}
        </div>
      )}

      {/* Ledige datoer */}
      <div className="mt-3">
        <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-1.5">
          {t("collab.available_dates")}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {item.available_slots.slice(0, 12).map((s) => (
            <button key={s.id} onClick={() => onCompose(s)}
              className="px-2 py-1 border border-white/15 text-xs text-[var(--cream-soft)] hover:border-[var(--magenta)] hover:text-[var(--magenta)] inline-flex items-center gap-1.5"
              data-testid={`find-slot-${s.date}`}>
              {s.date}
              <Send className="h-3 w-3" />
            </button>
          ))}
          {item.available_slots.length > 12 && (
            <span className="px-2 py-1 text-xs text-[var(--cream-faint)]">
              +{item.available_slots.length - 12}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}
